import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface FeedbackCardProps {
  title: string;
  description: string;
  completed: Date;
  feedbackId?: string;
  transcript_id?: number;
}

function formatShortDate(date: Date) {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  title,
  description,
  completed,
  feedbackId,
  transcript_id
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          position: 'relative',
          boxShadow: 2,
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
            transition: 'all 0.2s ease-in-out',
          }
        }}
        onClick={() => {
          if (feedbackId) {
            const url = transcript_id !== undefined && transcript_id !== null
              ? `/feedback/${feedbackId}?transcript_id=${transcript_id}`
              : `/feedback/${feedbackId}`;
            navigate(url);
          }
        }}
      >
        {/* Left column: Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 96,
            bgcolor: 'transparent',
            px: 2
          }}
        >
          <Box sx={{
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
            borderRadius: '50%',
            '& svg': { color: 'white', fontSize: 32 }
          }}>
            <AssessmentIcon />
          </Box>
        </Box>
        {/* Right column: Info */}
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            p: 3,
            position: 'relative'
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: .1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1}}>
            {description}
          </Typography>
          <Box sx={{ position: 'absolute', top: '50%', right: 24, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {completed && (
            <Chip
              icon={<CalendarIcon sx={{ fontSize: 36 }}/>}
              label={`Completed ${formatShortDate(completed)}`}
              color="default"
              size="small"
              sx={{ fontSize: '1.1rem', fontWeight: 500 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackCard;